import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Section, SectionHeading, InfoBox } from "@/components/Primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LeadForm from "@/components/LeadForm";
import { COUNTRIES } from "@/data/content";
import NotFound from "@/pages/NotFound";

const COUNTRY_FAQS = [
  { q: "How do I start?", a: "Begin with a confidential assessment of your business and goals. Contact us to know more." },
  { q: "What does it cost?", a: "Contact us to know more." },
  { q: "Is any outcome guaranteed?", a: "No outcome is guaranteed. Company formation, banking and immigration depend on your circumstances and decisions by third parties and authorities." },
  { q: "Do you handle immigration directly?", a: "Immigration matters are handled by appropriately licensed immigration professionals in the relevant country. We coordinate the business side." },
];

const Tick = ({ children }) => (<li className="flex items-start gap-3 text-sm text-navy"><span className="w-5 h-5 rounded-full bg-emerald-brand/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-emerald-brand" /></span>{children}</li>);

export default function CountryDetail() {
  const { slug } = useParams();
  const country = COUNTRIES.find((c) => c.slug === slug);
  if (!country) return <NotFound />;

  return (
    <>
      <SEO title={`${country.name} Business Services`} description={`${country.tagline}. ${country.profile} Contact us to know more.`} path={`/countries/${slug}`} />
      <PageHero eyebrow={`${country.flag} ${country.name}`} image={country.image} title={`${country.name} Business Services`} subtitle={country.tagline} />

      <Section className="bg-white">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <SectionHeading eyebrow="Overview" title={`Doing Business in ${country.name}`} subtitle={`Typical client profile: ${country.profile}`} />
              <h3 className="font-bold text-navy mb-3">Main Services</h3>
              <ul className="grid sm:grid-cols-2 gap-3">{country.services.map((s) => <Tick key={s}>{s}</Tick>)}</ul>
              <div className="mt-6"><InfoBox>Business setup, market-entry, business purchase and investment support are coordinated with our professional partner network. The appropriate approach depends on your business and circumstances. Contact us to know more.</InfoBox></div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-navy mb-4">Frequently Asked Questions</h3>
              <Accordion type="single" collapsible>
                {COUNTRY_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`c-faq-${i}`}>
                    <AccordionTrigger className="text-left text-navy font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
          <aside>
            <div className="sticky top-28 rounded-2xl border border-slate-100 shadow-lg p-6">
              <h3 className="font-bold text-navy mb-4">{country.name} Inquiry</h3>
              <LeadForm formName={`${country.name} Inquiry`} countryKey={country.key} testid={`country-form-${country.key}`} />
            </div>
          </aside>
        </div>
      </Section>

      <div className="bg-slate-50 py-10 text-center">
        <Link to="/countries" className="text-emerald-brand font-semibold hover:underline">← Back to all countries</Link>
      </div>
    </>
  );
}
